import axios, { AxiosError, AxiosResponse, CancelToken } from 'axios';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import { DefaultGetAllResponse, DefaultGetResponse } from '~/App/Api/rest';
import usePagination, { PaginationOptions } from '~/react-hooks/usePagination';
import { Recording, RecordingType } from '~/types/models';
import type {
  CreateRecordingResponse,
  UploadUrlResponse,
} from '~/types/api/recordings';

export interface RecordingFilters {
  page?: number;
  perPage?: number;
  types?: RecordingType[];
}

export interface GetRecordingsArgs {
  page?: number;
  per_page?: number;
  type?: string;
}

export const recordingsQueryKeys = {
  all: ['recordings'],
  lists: () => [...recordingsQueryKeys.all, 'list'] as const,
  list: (filters: RecordingFilters) =>
    [...recordingsQueryKeys.lists(), { ...filters }] as const,
  details: () => [...recordingsQueryKeys.all, 'detail'] as const,
  detail: (recordingId: string) =>
    [...recordingsQueryKeys.details(), recordingId] as const,
  shareLibraryUploadCount: () => [
    ...recordingsQueryKeys.all,
    'share-library-upload-count',
  ],
};

const apiUrl = '/api/v2/recordings';

export const getRecordings = async (params: GetRecordingsArgs) => {
  const response = await axios.get<DefaultGetAllResponse<Recording>>(apiUrl, {
    params,
  });

  return response.data;
};

export const getRecording = async (recordingId: string) => {
  const response = await axios.get<Recording>(
    `/api/v2/recordings/${recordingId}`
  );

  return response.data;
};

export const getRecordingUploadUrl = (id: string) => {
  return axios.get<UploadUrlResponse>(`${apiUrl}/${id}/upload-url`);
};

type CreateRecordingParams = {
  type: RecordingType;
};

export const createRecording = (params: CreateRecordingParams) => {
  return axios.post<CreateRecordingResponse>(apiUrl, params);
};

type GetShareLibraryUploadCountResponse = { count: number };
export const getShareLibraryUploadCount = async () => {
  const response = await axios.get<GetShareLibraryUploadCountResponse>(
    `${apiUrl}/share-library-count`
  );

  return response.data;
};

export interface UpdateRecordingArgs {
  id: string;
  name?: string;
  thumbnail?: Blob;
  singleUpload?: boolean;
  messageTemplateUuid?: string;
  cancelToken?: CancelToken;
  type?: RecordingType;
}

export interface UpdateRecordingResponse {
  message: string;
  data: Recording;
}

export const updateRecording = ({
  id,
  name,
  thumbnail,
  singleUpload,
  messageTemplateUuid,
  cancelToken,
  type,
}: UpdateRecordingArgs) => {
  const formData = new FormData();
  formData.append('_method', 'PUT');

  if (name) {
    formData.append('name', name);
  }

  if (thumbnail) {
    formData.append('thumbnail', thumbnail, 'thumbnail.jpg');
  }

  if (singleUpload) {
    formData.append('single_upload', 'true');
  }

  if (messageTemplateUuid) {
    formData.append('message_template_uuid', messageTemplateUuid);
  }

  if (type) {
    formData.append('type', type);
  }

  return axios.post<UpdateRecordingResponse>(`${apiUrl}/${id}`, formData, {
    cancelToken,
  });
};

export interface DeleteRecordingArgs {
  id: string;
}

export interface DeleteRecordingResponse {
  message: string;
}

export const deleteRecording = ({ id }: DeleteRecordingArgs) =>
  axios.delete<DeleteRecordingResponse>(`${apiUrl}/${id}`);

export interface GetRecordingsHookParams {
  filters: RecordingFilters;
  options?: UseQueryOptions<DefaultGetAllResponse<Recording>, AxiosError>;
}

export const useRecordings = (params: GetRecordingsHookParams) => {
  return useQuery<DefaultGetAllResponse<Recording>, AxiosError>(
    recordingsQueryKeys.list(params.filters),
    () => {
      const { page, perPage, types } = params.filters;
      return getRecordings({ page, per_page: perPage, type: types?.join() });
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      suspense: false,
      ...params.options,
    }
  );
};

export const useRecording = (recordingId: string) => {
  return useQuery<DefaultGetResponse<Recording>, AxiosError>(
    recordingsQueryKeys.detail(recordingId),
    () => {
      return getRecording(recordingId);
    },
    {
      refetchOnWindowFocus: false,
    }
  );
};

export const useShareLibraryUploadCount = () => {
  return useQuery<
    DefaultGetResponse<GetShareLibraryUploadCountResponse>,
    AxiosError
  >(recordingsQueryKeys.shareLibraryUploadCount(), getShareLibraryUploadCount, {
    refetchOnWindowFocus: false,
  });
};

export function useUpdateRecording(
  options?: UseMutationOptions<
    AxiosResponse<UpdateRecordingResponse>,
    AxiosError,
    UpdateRecordingArgs
  >
) {
  const queryClient = useQueryClient();

  return useMutation(updateRecording, {
    onSuccess: () => queryClient.invalidateQueries(recordingsQueryKeys.lists()),
    ...options,
  });
}

export function useDeleteRecording(
  options?: UseMutationOptions<
    AxiosResponse<DeleteRecordingResponse>,
    AxiosError,
    DeleteRecordingArgs
  >
) {
  const queryClient = useQueryClient();

  return useMutation(deleteRecording, {
    onSuccess: () => {
      void queryClient.invalidateQueries(recordingsQueryKeys.lists());
    },
    ...options,
  });
}

export function useRecordingsPagination(params: PaginationOptions) {
  return usePagination<Recording>(apiUrl, params);
}
